import React, { useRef, useState, useCallback, useEffect, ReactNode } from 'react';
import { CobuStack, CobuText } from '@cobu/ui';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from 'tamagui';
import { Strings } from '../../constants/Strings';

const pullToRefreshStrings = Strings.get().ui.pullToRefresh;

interface PullToRefreshWrapperProps {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  isRefreshing?: boolean;
  threshold?: number; // Distance in pixels to trigger refresh
  disabled?: boolean;
  pullText?: string;
  releaseText?: string;
  refreshingText?: string;
  useWindowScroll?: boolean; // Whether Virtuoso uses window scroll
}

const DEFAULT_THRESHOLD = 80;
const MAX_PULL_DISTANCE = 120;

// A pull gesture must never start on a native control — mousedown's default
// action is what opens a <select>/focuses an input, and preventing it (below)
// would swallow the tap. Bail out when the gesture originates on one of these.
const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  !!target.closest('select, input, textarea, button, a, [role="button"]');

export const PullToRefreshWrapper: React.FC<PullToRefreshWrapperProps> = ({
  children,
  onRefresh,
  isRefreshing = false,
  threshold = DEFAULT_THRESHOLD,
  disabled = false,
  pullText = pullToRefreshStrings.pull,
  releaseText = pullToRefreshStrings.release,
  refreshingText = pullToRefreshStrings.refreshing,
  useWindowScroll = false,
}) => {
  const theme = useTheme();
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [startY, setStartY] = useState(0);
  const [canPull, setCanPull] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  // Check if we're at the top of the scrollable container
  const checkIfAtTop = useCallback(() => {
    if (useWindowScroll) {
      // Check window scroll position
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      return scrollTop === 0;
    } else {
      // Check container scroll position
      if (!containerRef.current) return false;
      const scrollTop = containerRef.current.scrollTop || 0;
      return scrollTop === 0;
    }
  }, [useWindowScroll]);

  // Handle touch start
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled || isRefreshing) return;
    if (isInteractiveTarget(e.target)) return;

    const atTop = checkIfAtTop();
    if (atTop) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
      setCanPull(true);
      isDraggingRef.current = true;
    }
  }, [disabled, isRefreshing, checkIfAtTop]);

  // Handle mouse down
  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (disabled || isRefreshing) return;
    if (isInteractiveTarget(e.target)) return;

    const atTop = checkIfAtTop();
    if (atTop) {
      setStartY(e.clientY);
      setIsPulling(true);
      setCanPull(true);
      isDraggingRef.current = true;
      e.preventDefault();
    }
  }, [disabled, isRefreshing, checkIfAtTop]);

  // Handle touch move
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDraggingRef.current || !canPull || disabled || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;
    
    if (deltaY > 0 && checkIfAtTop()) {
      // Prevent default scrolling while pulling
      e.preventDefault();
      const distance = Math.min(deltaY, MAX_PULL_DISTANCE);
      setPullDistance(distance);
    } else {
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [canPull, startY, disabled, isRefreshing, checkIfAtTop]);

  // Handle mouse move
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !canPull || disabled || isRefreshing) return;
    
    const currentY = e.clientY;
    const deltaY = currentY - startY;
    
    if (deltaY > 0 && checkIfAtTop()) {
      // Prevent default scrolling while pulling
      e.preventDefault();
      const distance = Math.min(deltaY, MAX_PULL_DISTANCE);
      setPullDistance(distance);
    } else {
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [canPull, startY, disabled, isRefreshing, checkIfAtTop]);

  // Handle touch end / mouse up
  const handleEnd = useCallback(async () => {
    if (!isDraggingRef.current) return;
    
    isDraggingRef.current = false;
    
    if (pullDistance >= threshold && !isRefreshing) {
      // Trigger refresh
      await onRefresh();
    }
    
    // Reset state
    setIsPulling(false);
    setPullDistance(0);
    setCanPull(false);
    setStartY(0);
  }, [pullDistance, threshold, isRefreshing, onRefresh]);

  // Set up event listeners
  useEffect(() => {
    if (useWindowScroll) {
      // For window scroll, attach to document body
      const target = document.body;
      
      // Touch events
      target.addEventListener('touchstart', handleTouchStart, { passive: false });
      target.addEventListener('touchmove', handleTouchMove, { passive: false });
      target.addEventListener('touchend', handleEnd);
      target.addEventListener('touchcancel', handleEnd);

      // Mouse events (for desktop)
      target.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);

      return () => {
        target.removeEventListener('touchstart', handleTouchStart);
        target.removeEventListener('touchmove', handleTouchMove);
        target.removeEventListener('touchend', handleEnd);
        target.removeEventListener('touchcancel', handleEnd);
        target.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleEnd);
      };
    } else {
      // For container scroll, attach to container
      const container = containerRef.current;
      if (!container) return;

      // Touch events
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleEnd);
      container.addEventListener('touchcancel', handleEnd);

      // Mouse events (for desktop)
      container.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);

      return () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleEnd);
        container.removeEventListener('touchcancel', handleEnd);
        container.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleEnd);
      };
    }
  }, [useWindowScroll, handleTouchStart, handleTouchMove, handleMouseDown, handleMouseMove, handleEnd]);

  // Reset pull distance when refreshing starts
  useEffect(() => {
    if (isRefreshing) {
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [isRefreshing]);

  // Reset state on mount/unmount to prevent showing indicator when switching tabs
  useEffect(() => {
    // Reset all state when component mounts
    setIsPulling(false);
    setPullDistance(0);
    setCanPull(false);
    setStartY(0);
    isDraggingRef.current = false;
  }, []);

  // Show spinner only during actual pull gesture (with distance > 0) or when refreshing
  const showIndicator = (isPulling && pullDistance > 0) || isRefreshing;
  // Calculate spinner position based on pull distance
  const spinnerOffset = isPulling ? Math.max(0, pullDistance - 20) : 0;
  // Calculate rotation angle based on pull distance (rotate up to 180 degrees)
  const pullRotation = isPulling ? Math.min((pullDistance / threshold) * 180, 180) : 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Pull to refresh indicator - shown during pull and refresh */}
      {showIndicator && (
        <CobuStack
          position="absolute"
          top={0}
          left={0}
          right={0}
          ai="center"
          jc="center"
          py="$3"
          bg="transparent"
          style={{
            transform: `translateY(${spinnerOffset}px)`,
            transition: isRefreshing ? 'transform 0.3s ease-out' : 'none',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <CobuStack ai="center" jc="center" py="$3">
            <CobuStack
              ai="center"
              jc="center"
              width={48}
              height={48}
              borderRadius={24}
              bg="#f6f6f6"
            >
              <RefreshIcon
                fontSize="large"
                htmlColor={theme.cobuDarkBlue?.val || '#1a365d'}
                style={{
                  transform: isRefreshing ? undefined : `rotate(${pullRotation}deg)`,
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  transition: isRefreshing ? 'none' : 'transform 0.1s ease-out',
                }}
              />
            </CobuStack>
            {isRefreshing && (
              <style>{`
                @keyframes spin {
                  from { transform: rotate(${pullRotation}deg); }
                  to { transform: rotate(${pullRotation + 360}deg); }
                }
              `}</style>
            )}
          </CobuStack>
        </CobuStack>
      )}
      
      {/* Children content */}
      {children}
    </div>
  );
};

